import type {
  ClassicScoringRuleSetting,
  League,
  TipRound,
  TipRoundCompetition,
  TipRoundGameMode,
  TipRoundMembership,
} from "../types/index.ts";

const SUPABASE_URL = "https://fvuwfonfrllbjqvywula.supabase.co";

// Hybrid REST pattern (same as TournamentService): raw fetch + anon key for
// public reads, plus the user's access_token (lifted from localStorage) for
// authenticated writes. The bundled supabase v2.0.0 client hangs on .from()
// calls in this environment, so we drive PostgREST directly.

const anonHeaders = (): Record<string, string> => ({
  apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

const PROJECT_REF = "fvuwfonfrllbjqvywula";
const SESSION_STORAGE_KEY = `sb-${PROJECT_REF}-auth-token`;

const getAccessTokenFromStorage = (): string | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (
      parsed?.access_token ??
      parsed?.currentSession?.access_token ??
      null
    );
  } catch {
    return null;
  }
};

const authedHeaders = (): Record<string, string> => {
  const token =
    getAccessTokenFromStorage() ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

// 8-char alphanumeric invite codes (no ambiguous chars: 0/O, 1/I/l).
const INVITE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const generateInviteCode = (): string => {
  let s = "";
  for (let i = 0; i < 8; i++) {
    s += INVITE_ALPHABET[Math.floor(Math.random() * INVITE_ALPHABET.length)];
  }
  return s;
};

export interface TipRoundDetail {
  round: TipRound;
  members: TipRoundMembership[];
  competitions: League[];
  memberCount: number;
}

export class TipRoundService {
  private static instance: TipRoundService;
  private constructor() {}

  public static getInstance(): TipRoundService {
    if (!TipRoundService.instance) {
      TipRoundService.instance = new TipRoundService();
    }
    return TipRoundService.instance;
  }

  // ---- Tip rounds ---------------------------------------------------------

  /** All tip rounds on the platform — admin only (RLS: email = 'v1cke@online.dev'). */
  async listAll(): Promise<TipRound[]> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_rounds?select=*&order=created_at.desc`,
        { method: "GET", headers: authedHeaders() },
      );
      if (!res.ok) return [];
      return (await res.json()) as TipRound[];
    } catch (e) {
      console.error("TipRoundService.listAll failed:", e);
      return [];
    }
  }

  /** All tip rounds the given user is a member of, ordered newest first. */
  async listForUser(userId: string): Promise<TipRound[]> {
    try {
      // Step 1: get membership rows for this user
      const mRes = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships?user_id=eq.${userId}&select=tip_round_id`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!mRes.ok) return [];
      const memberships = (await mRes.json()) as Array<{ tip_round_id: number }>;
      if (memberships.length === 0) return [];
      const ids = memberships.map((m) => m.tip_round_id);

      // Step 2: load the rounds
      const rRes = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_rounds?id=in.(${ids.join(",")})&select=*&order=created_at.desc`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!rRes.ok) return [];
      return (await rRes.json()) as TipRound[];
    } catch (e) {
      console.error("TipRoundService.listForUser failed:", e);
      return [];
    }
  }

  async getById(tipRoundId: number): Promise<TipRound | null> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_rounds?id=eq.${tipRoundId}&select=*`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!res.ok) return null;
      const rows = (await res.json()) as TipRound[];
      return rows[0] ?? null;
    } catch (e) {
      console.error("TipRoundService.getById failed:", e);
      return null;
    }
  }

  async findByInviteCode(code: string): Promise<TipRound | null> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_rounds?invite_code=eq.${encodeURIComponent(code.toUpperCase())}&select=*`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!res.ok) return null;
      const rows = (await res.json()) as TipRound[];
      return rows[0] ?? null;
    } catch (e) {
      console.error("TipRoundService.findByInviteCode failed:", e);
      return null;
    }
  }

  /**
   * Creates a new private tip round owned by the given user + joins them.
   * The owner is auto-joined as 'owner'.
   *
   * gameMode is fixed for the whole round's lifetime (Migration 042):
   * 'tabellen' (default) = official table is the league-table tip;
   * 'klassisch' = official table is the classic single-match tip, with the
   * league-table tip becoming an optional add-on. Not applicable to
   * TOURNAMENT/CUP competitions (WM/CL), which have no classic mode.
   */
  async create(
    ownerUserId: string,
    name: string,
    description: string | null,
    gameMode: TipRoundGameMode = "tabellen",
  ): Promise<TipRound | null> {
    const payload: Record<string, unknown> = {
      name,
      description,
      owner_user_id: ownerUserId,
      invite_code: generateInviteCode(),
      is_public: false,
      is_system: false,
      game_mode: gameMode,
    };
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_rounds`,
        {
          method: "POST",
          headers: {
            ...authedHeaders(),
            Prefer: "return=representation",
          },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        console.error(
          "TipRoundService.create failed:",
          res.status,
          await res.text(),
        );
        return null;
      }
      const rows = (await res.json()) as TipRound[];
      const round = rows[0];
      if (!round) return null;
      // Auto-join the creator as 'owner'
      await this.join(round.id, ownerUserId, "owner");
      return round;
    } catch (e) {
      console.error("TipRoundService.create failed:", e);
      return null;
    }
  }

  /** Owner can rename / re-describe a round. */
  async updateRound(
    tipRoundId: number,
    patch: { name?: string; description?: string | null },
  ): Promise<TipRound | null> {
    const body: Record<string, unknown> = {};
    if (patch.name !== undefined) body.name = patch.name.trim();
    if (patch.description !== undefined) {
      body.description = patch.description?.trim() || null;
    }
    return this._patchRound(tipRoundId, body);
  }

  /**
   * Owner can update join mode, payment info (Migration 029), and/or the
   * classic scoring rule (Migration 042). game_mode itself is intentionally
   * NOT patchable here — it's fixed at creation for the round's lifetime
   * (changing it after tips exist would silently reinterpret which account
   * is official). classic_scoring_rule only matters while game_mode is
   * 'klassisch', but can be set any time before the season lock.
   */
  async updateRoundSettings(
    tipRoundId: number,
    patch: {
      join_mode?: "auto" | "approval";
      payment_info?: import("../types/index.ts").PaymentInfo | null;
      classic_scoring_rule?: ClassicScoringRuleSetting;
    },
  ): Promise<TipRound | null> {
    const body: Record<string, unknown> = {};
    if (patch.join_mode !== undefined) body.join_mode = patch.join_mode;
    if (patch.payment_info !== undefined) body.payment_info = patch.payment_info;
    if (patch.classic_scoring_rule !== undefined)
      body.classic_scoring_rule = patch.classic_scoring_rule;
    return this._patchRound(tipRoundId, body);
  }

  private async _patchRound(tipRoundId: number, body: Record<string, unknown>): Promise<TipRound | null> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_rounds?id=eq.${tipRoundId}`,
        {
          method: "PATCH",
          headers: { ...authedHeaders(), Prefer: "return=representation" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        console.error("TipRoundService._patchRound failed:", res.status);
        return null;
      }
      const rows = (await res.json()) as TipRound[];
      return rows[0] ?? null;
    } catch (e) {
      console.error("TipRoundService._patchRound error:", e);
      return null;
    }
  }

  // ---- Memberships --------------------------------------------------------

  /** Join a round. Returns 'joined', 'pending', or false on error.
   *  Pass status='pending' when the round requires admin approval. */
  /**
   * Join a round, or report that the user is already in it.
   *
   * An existing membership is checked first and then left completely alone —
   * two reasons, both from re-opening an invite link that is already used:
   *
   *   • The upsert would turn into an UPDATE, which members have no policy
   *     for (only insert/delete). The call failed and the user was told the
   *     join had failed, although they were long since a member.
   *   • For the round owner the UPDATE *does* pass (Migration 029) and would
   *     write role='member', demoting them in the member list.
   */
  async join(
    tipRoundId: number,
    userId: string,
    role: "owner" | "member" = "member",
    status: "active" | "pending" = "active",
  ): Promise<"joined" | "pending" | "already" | false> {
    try {
      const existing = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships` +
          `?tip_round_id=eq.${tipRoundId}&user_id=eq.${userId}&select=status`,
        { method: "GET", headers: authedHeaders() },
      );
      if (existing.ok) {
        const rows = (await existing.json()) as Array<{ status: string }>;
        if (rows.length > 0) return "already";
      }

      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships?on_conflict=tip_round_id,user_id`,
        {
          method: "POST",
          headers: {
            ...authedHeaders(),
            Prefer: "resolution=merge-duplicates,return=minimal",
          },
          body: JSON.stringify({ tip_round_id: tipRoundId, user_id: userId, role, status }),
        },
      );
      if (!res.ok) return false;
      return status === "pending" ? "pending" : "joined";
    } catch (e) {
      console.error("TipRoundService.join failed:", e);
      return false;
    }
  }

  /** Approve a pending member (owner only). */
  async approveMember(tipRoundId: number, userId: string): Promise<boolean> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships?tip_round_id=eq.${tipRoundId}&user_id=eq.${userId}`,
        {
          method: "PATCH",
          headers: { ...authedHeaders(), Prefer: "return=minimal" },
          body: JSON.stringify({ status: "active" }),
        },
      );
      return res.ok;
    } catch (e) {
      console.error("TipRoundService.approveMember failed:", e);
      return false;
    }
  }

  /** Reject a pending member or kick an active member (owner only). */
  async removeMember(tipRoundId: number, userId: string): Promise<boolean> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships?tip_round_id=eq.${tipRoundId}&user_id=eq.${userId}`,
        { method: "DELETE", headers: authedHeaders() },
      );
      return res.ok;
    } catch (e) {
      console.error("TipRoundService.removeMember failed:", e);
      return false;
    }
  }

  /** Loads the current user's membership in a given round (or null). */
  async getMembership(
    tipRoundId: number,
    userId: string,
  ): Promise<TipRoundMembership | null> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships?tip_round_id=eq.${tipRoundId}&user_id=eq.${userId}&select=*`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!res.ok) return null;
      const rows = (await res.json()) as TipRoundMembership[];
      return rows[0] ?? null;
    } catch (e) {
      console.error("TipRoundService.getMembership failed:", e);
      return null;
    }
  }

  async leave(tipRoundId: number, userId: string): Promise<boolean> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships?tip_round_id=eq.${tipRoundId}&user_id=eq.${userId}`,
        { method: "DELETE", headers: authedHeaders() },
      );
      return res.ok;
    } catch (e) {
      console.error("TipRoundService.leave failed:", e);
      return false;
    }
  }

  /** Active members only — used for leaderboard/member display. */
  async listMembers(tipRoundId: number): Promise<TipRoundMembership[]> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships?tip_round_id=eq.${tipRoundId}&status=eq.active&select=*&order=joined_at`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!res.ok) return [];
      return (await res.json()) as TipRoundMembership[];
    } catch (e) {
      console.error("TipRoundService.listMembers failed:", e);
      return [];
    }
  }

  /** Pending members only — owner-only view for approvals. */
  async listPendingMembers(tipRoundId: number): Promise<TipRoundMembership[]> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_memberships?tip_round_id=eq.${tipRoundId}&status=eq.pending&select=*&order=joined_at`,
        { method: "GET", headers: authedHeaders() },
      );
      if (!res.ok) return [];
      return (await res.json()) as TipRoundMembership[];
    } catch (e) {
      console.error("TipRoundService.listPendingMembers failed:", e);
      return [];
    }
  }

  // ---- Competitions in a round -------------------------------------------

  async listCompetitions(tipRoundId: number): Promise<League[]> {
    try {
      const linkRes = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_competitions?tip_round_id=eq.${tipRoundId}&select=competition_id`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!linkRes.ok) return [];
      const links = (await linkRes.json()) as TipRoundCompetition[];
      if (links.length === 0) return [];
      const ids = links.map((l) => l.competition_id);
      // Only return active competitions — finished seasons (bl1/bl2/pl 25/26)
      // are flagged is_active=false via Migration 012 and must not appear in
      // the LeagueSelector or any round-internal competition list.
      const compRes = await fetch(
        `${SUPABASE_URL}/rest/v1/leagues?id=in.(${ids.join(",")})&is_active=eq.true&select=*&order=display_order`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!compRes.ok) return [];
      return (await compRes.json()) as League[];
    } catch (e) {
      console.error("TipRoundService.listCompetitions failed:", e);
      return [];
    }
  }

  /**
   * Competitions the user still owes a tip for, across the given rounds.
   *
   * Tips are per round, not per competition — the same league in two rounds
   * needs two tips. So this compares every (round, competition) pair the user
   * is signed up for against the tips actually submitted, and returns the
   * competition ids where at least one pair is still missing.
   *
   * Covers both storage shapes: league tables in tips_v2 (league_id),
   * tournament and cup tips in tips_tournament (competition_id).
   *
   * On error it returns an empty set — the reminder then stays quiet rather
   * than nagging about competitions that may well be done.
   */
  async listCompetitionsNeedingTip(
    userId: string,
    tipRoundIds: number[],
  ): Promise<Set<number>> {
    const needsTip = new Set<number>();
    if (tipRoundIds.length === 0) return needsTip;

    try {
      const rounds = tipRoundIds.join(",");
      const [linkRes, leagueTipRes, tournamentTipRes] = await Promise.all([
        fetch(
          `${SUPABASE_URL}/rest/v1/tip_round_competitions?tip_round_id=in.(${rounds})` +
            `&select=tip_round_id,competition_id`,
          { method: "GET", headers: anonHeaders() },
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/tips_v2?tip_round_id=in.(${rounds})` +
            `&user_id=eq.${userId}&select=tip_round_id,league_id`,
          { method: "GET", headers: authedHeaders() },
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/tips_tournament?tip_round_id=in.(${rounds})` +
            `&user_id=eq.${userId}&select=tip_round_id,competition_id`,
          { method: "GET", headers: authedHeaders() },
        ),
      ]);
      if (!linkRes.ok) return needsTip;

      const links = (await linkRes.json()) as Array<{
        tip_round_id: number;
        competition_id: number;
      }>;

      const done = new Set<string>();
      if (leagueTipRes.ok) {
        for (
          const r of (await leagueTipRes.json()) as Array<
            { tip_round_id: number; league_id: number }
          >
        ) {
          done.add(`${r.tip_round_id}:${r.league_id}`);
        }
      }
      if (tournamentTipRes.ok) {
        for (
          const r of (await tournamentTipRes.json()) as Array<
            { tip_round_id: number; competition_id: number }
          >
        ) {
          done.add(`${r.tip_round_id}:${r.competition_id}`);
        }
      }

      for (const link of links) {
        if (!done.has(`${link.tip_round_id}:${link.competition_id}`)) {
          needsTip.add(link.competition_id);
        }
      }
    } catch (e) {
      console.error("TipRoundService.listCompetitionsNeedingTip failed:", e);
    }
    return needsTip;
  }

  async addCompetition(
    tipRoundId: number,
    competitionId: number,
  ): Promise<boolean> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_competitions?on_conflict=tip_round_id,competition_id`,
        {
          method: "POST",
          headers: {
            ...authedHeaders(),
            Prefer: "resolution=merge-duplicates,return=minimal",
          },
          body: JSON.stringify({
            tip_round_id: tipRoundId,
            competition_id: competitionId,
          }),
        },
      );
      return res.ok;
    } catch (e) {
      console.error("TipRoundService.addCompetition failed:", e);
      return false;
    }
  }

  async removeCompetition(
    tipRoundId: number,
    competitionId: number,
  ): Promise<boolean> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tip_round_competitions?tip_round_id=eq.${tipRoundId}&competition_id=eq.${competitionId}`,
        { method: "DELETE", headers: authedHeaders() },
      );
      return res.ok;
    } catch (e) {
      console.error("TipRoundService.removeCompetition failed:", e);
      return false;
    }
  }
}

export const tipRoundService = TipRoundService.getInstance();
